/**
 * useQuestStore — Quarterly Constraint Engine (GPS Method)
 *
 * A Quest = one strategic objective for a quarter.
 * Each domain (work/life) has: 1 Main Quest + up to 3 Side Quests.
 *
 * GPS = Goal / Plan / System
 *   Goal   → What exactly do I want to achieve?
 *   Plan   → How will I get there? (milestones)
 *   System → What daily/weekly habits power it?
 *
 * Schema:
 *   id            string  (UUID)
 *   name          string
 *   domain        'work' | 'life'
 *   type          'main' | 'side'
 *   status        'active' | 'completed' | 'abandoned'
 *   goal          string   — GPS: Goal
 *   plan          string   — GPS: Plan (milestones)
 *   system        string   — GPS: System (habits)
 *   progress      number   — 0–100 (manual %)
 *   quarterStart  string   — ISO date (YYYY-MM-DD)
 *   quarterEnd    string   — ISO date
 *   projectIds    string[] — linked projects
 *   createdAt     string   — ISO datetime
 *   updatedAt     string   — ISO datetime
 *
 * Storage: 'quests' (collection key)
 */
import { create } from 'zustand';
import { storage, onRehydrate } from '../utils/storage';
import { localDateStr } from '../utils/dateUtils';

const STORAGE_KEY = 'quests';

function currentQuarterBounds() {
    const now = new Date();
    const q = Math.floor(now.getMonth() / 3);
    const start = new Date(now.getFullYear(), q * 3, 1);
    const end = new Date(now.getFullYear(), q * 3 + 3, 0); // last day of quarter
    return {
        quarterStart: localDateStr(start),
        quarterEnd: localDateStr(end),
    };
}

const useQuestStore = create((set, get) => ({
    quests: storage.get(STORAGE_KEY, []),

    _persist() {
        storage.set(STORAGE_KEY, get().quests);
    },

    addQuest(quest) {
        const bounds = currentQuarterBounds();
        const newQuest = {
            id: crypto.randomUUID(),
            name: quest.name || 'New Quest',
            domain: quest.domain || 'work',
            type: quest.type || 'main',
            status: 'active',
            goal: quest.goal || '',
            plan: quest.plan || '',
            system: quest.system || '',
            progress: 0,
            quarterStart: quest.quarterStart || bounds.quarterStart,
            quarterEnd: quest.quarterEnd || bounds.quarterEnd,
            projectIds: quest.projectIds || [],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        };
        set(state => ({ quests: [...state.quests, newQuest] }));
        get()._persist();
        return newQuest;
    },

    updateQuest(id, updates) {
        set(state => ({
            quests: state.quests.map(q =>
                q.id === id ? { ...q, ...updates, updatedAt: new Date().toISOString() } : q
            ),
        }));
        get()._persist();
    },

    deleteQuest(id) {
        set(state => ({ quests: state.quests.filter(q => q.id !== id) }));
        get()._persist();
    },

    setProgress(id, progress) {
        get().updateQuest(id, { progress: Math.max(0, Math.min(100, progress)) });
    },

    completeQuest(id) {
        get().updateQuest(id, { status: 'completed', progress: 100 });
        // Fire ritual event trigger for quest-complete
        window.dispatchEvent(new CustomEvent('ritual-trigger', { detail: { eventType: 'quest-complete' } }));
    },

    abandonQuest(id) {
        get().updateQuest(id, { status: 'abandoned' });
    },

    /**
     * Get quests for the current quarter (both domains).
     */
    getCurrentQuests() {
        const { quarterStart } = currentQuarterBounds();
        return get().quests.filter(q =>
            q.quarterStart === quarterStart && q.status !== 'abandoned'
        );
    },
}));

onRehydrate(() => {
    useQuestStore.setState({ quests: storage.get(STORAGE_KEY, []) });
});

export default useQuestStore;
export { currentQuarterBounds };
